interface Options {
  collectionSize: number;
  animation?: number;
  gutter?: Ref<number>;
  freeWheelFullScroll?: boolean;
}

/**
 * Warning - At this stage, the "current" element is still in its "collapsed" form
 * @param container
 * @param heightBuffer
 * @param currentStep
 * @param prevStep
 * @returns
 */
function computeTranslate(
  container: Ref<HTMLElement | undefined>,
  heightBuffer: Ref<number[]>,
  currentStep: number,
  gutter: Ref<number> | undefined,
): number {
  if (!container.value) return 0;

  const children = Array.from(container.value.children) as HTMLElement[];

  const firstItemHeight = heightBuffer.value[0];
  const containerHeight = container.value.clientHeight;

  const initialTranslate = containerHeight / 2 - firstItemHeight / 2;

  /**
   * Find the non-current child height
   * 1. Get all the children that are not supposed to be current
   * 2. Create a map where the key is the height and the value is the # of elements of that height
   * 3. Get the key where their is the maximum number of element
   *
   * Why that ?
   * Because of Vue lifecycle and DOM reactivity, a non-current slide can become current
   * while inside this function, creating a weird behavior and offsetting based on the current
   * element height (which is bigger than its non-current counterpart)
   */
  const collapsedChildren = children.filter(
    child => !child.classList.contains('AiCarousel-slide--current'),
  );

  const collapsedChildHeightMap = collapsedChildren
    .map(child => child?.getBoundingClientRect()?.height)
    .reduce(
      (acc, height) => {
        if (typeof acc[height] !== 'number') {
          acc[height] = 0;
        }

        acc[height]++;

        return acc;
      },
      {} as Record<number, number>,
    );

  const collapsedSize = Object.keys(collapsedChildHeightMap).reduce((a, b) => {
    const height1 = Number(a);
    const height2 = Number(b);

    return collapsedChildHeightMap[height1] > collapsedChildHeightMap[height2]
      ? height1
      : height2;
  }, 0);

  const offset = children
    .slice(0, currentStep - 1)
    .reduce(
      sum => sum + collapsedSize + (gutter?.value ?? 0),
      -initialTranslate,
    );

  return offset * -1;
}

function computeHeightBuffer(
  container: Ref<HTMLElement | undefined>,
  gutter?: Ref<number>,
): number[] {
  if (!container.value) return [];

  const carousel = container.value.parentElement;

  if (!carousel) return [];

  const clone = container.value.cloneNode(true) as HTMLElement;
  clone.ariaDisabled = 'true';
  container.value.appendChild(clone);

  clone.style.position = 'absolute';
  clone.style.opacity = '0';
  clone.style.transform = 'translateX(-100vw)';
  clone.style.zIndex = '-1000';

  const cloneChildren = Array.from(clone.children) as HTMLElement[];
  for (const slide of cloneChildren) {
    const slideElement = slide.querySelector('.HotelExperienceCarouselItem');

    if (!slideElement) continue;

    slideElement.classList.remove(
      'HotelExperienceCarouselItem--previous',
      'HotelExperienceCarouselItem--next',
    );
    slideElement.classList.add('HotelExperienceCarouselItem--current');
  }

  const heightBuffer = cloneChildren.map(
    element => element.clientHeight + (gutter?.value ?? 0),
  );

  container.value.removeChild(clone);

  return heightBuffer;
}

/**
 * @param event
 * @param currentStep
 * @param collectionSize
 * @returns
 */
function canChangeStep(
  event: WheelEvent,
  currentStep: Ref<number>,
  collectionSize: number,
): boolean {
  const direction = Math.sign(event.deltaY);
  const nextStep = currentStep.value + direction;

  return nextStep > 0 && nextStep <= collectionSize;
}

export function useVerticalCarouselInteraction(
  container: Ref<HTMLElement | undefined>,
  currentStep: Ref<number>,
  { animation, collectionSize, gutter, freeWheelFullScroll }: Options,
) {
  const heightBuffer = ref<number[]>([]);
  const animating = ref<boolean>(false);
  const scrollWhileAnimating = ref<number>(0);

  onMounted(() => {
    if (container.value) {
      container.value.parentElement?.addEventListener(
        'wheel',
        handleStepChange,
      );
    }

    nextTick(() => {
      if (!container.value) return;

      heightBuffer.value = computeHeightBuffer(container, gutter);

      const translate = computeTranslate(
        container,
        heightBuffer,
        currentStep.value,
        gutter,
      );
      translateTo(translate, true, animation);
    });
  });

  onBeforeUnmount(() => {
    if (!container.value) return;

    container.value.parentElement?.removeEventListener(
      'wheel',
      handleStepChange,
    );
  });

  watch(currentStep, (step, prevStep) => {
    if (step === prevStep) return;

    const translate = computeTranslate(container, heightBuffer, step, gutter);

    translateTo(translate, true, animation);
    setTimeout(() => {
      scrollWhileAnimating.value = 0;
      animating.value = false;
    }, animation ?? 500);
  });

  const handleStepChange = (event: WheelEvent) => {
    const direction = Math.sign(event.deltaY);

    if (animating.value) {
      event.preventDefault();
      scrollWhileAnimating.value++;

      if (scrollWhileAnimating.value < 2 || !freeWheelFullScroll) return;

      if (direction > 0) {
        currentStep.value = collectionSize;
      } else {
        currentStep.value = 1;
      }

      return;
    }

    if (canChangeStep(event, currentStep, collectionSize)) {
      event.preventDefault();
      animating.value = true;

      const nextStep = currentStep.value + direction;

      if (nextStep < 1) {
        currentStep.value = 1;
      } else if (nextStep > collectionSize) {
        currentStep.value = collectionSize;
      } else {
        currentStep.value = nextStep;
      }
    }
  };

  const translateTo = (
    translation: number,
    animate = false,
    duration: number | null = null,
  ) => {
    if (!container.value) return;

    const transform = `translateY(${translation}px)`;
    const transition = animate
      ? `transform ${duration ?? animation ?? 300}ms`
      : 'none';

    container.value.style.transform = transform;
    container.value.style.transition = transition;
  };
}
